import React, { useEffect, useState } from 'react';
import {
  BookOpen,
  Loader2,
  CheckCircle,
  XCircle,
  Image as ImageIcon,
  HelpCircle,
} from 'lucide-react';
import { adminDb } from '../lib/adminDb';
import { CHAPTER_IDS, CHAPTER_TITLES_DE } from '@shared/chapters';

interface ChapterStats {
  id: number;
  title: string;
  questionCount: number;
  withImage: number;
  difficulties: { easy: number; medium: number; hard: number };
}

const CHAPTER_TITLES = CHAPTER_TITLES_DE;

export const ChaptersPage: React.FC = () => {
  const [chapters, setChapters] = useState<ChapterStats[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChapters();
  }, []);

  const loadChapters = async () => {
    setLoading(true);
    try {
      // Exclude image_placeholder to avoid transferring huge base64 data
      const { data, error } = await adminDb.from('questions').select('chapter_id, difficulty');
      if (error) throw error;

      // Lightweight check for image presence
      let idsWithImage = new Set<string>();
      try {
        const { data: imgData } = await adminDb.rpc('get_questions_with_images');
        idsWithImage = new Set((imgData || []).map((r: any) => r.id));
      } catch {
        /* RPC not available */
      }

      const stats: ChapterStats[] = CHAPTER_IDS.map((chId) => {
        const chQuestions = (data || []).filter((q) => q.chapter_id === chId);
        return {
          id: chId,
          title: CHAPTER_TITLES[chId] || `Kapitel ${chId}`,
          questionCount: chQuestions.length,
          withImage: chQuestions.filter((q: any) => idsWithImage.has(q.id)).length,
          difficulties: {
            easy: chQuestions.filter((q) => q.difficulty === 'Leicht').length,
            medium: chQuestions.filter((q) => q.difficulty === 'Mittel').length,
            hard: chQuestions.filter((q) => q.difficulty === 'Schwer').length,
          },
        };
      });
      setChapters(stats);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const totalQuestions = chapters.reduce((s, c) => s + c.questionCount, 0);
  const totalImages = chapters.reduce((s, c) => s + c.withImage, 0);

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h1>
            <BookOpen size={24} /> Kapitelmanagement
          </h1>
        </div>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>
          <BookOpen size={24} /> Kapitelmanagement
        </h1>
      </div>

      {/* Summary */}
      <div className="grid-cols-3 mb-6">
        <div className="kpi-card">
          <span className="kpi-label">Kapitel</span>
          <span className="kpi-value">{CHAPTER_IDS.length}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Fragen gesamt</span>
          <span className="kpi-value">{totalQuestions}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Bilder gesamt</span>
          <span className="kpi-value">{totalImages}</span>
          <span className="text-xs text-muted">
            {totalQuestions > 0 ? Math.round((totalImages / totalQuestions) * 100) : 0}% abgedeckt
          </span>
        </div>
      </div>

      {/* Chapter Cards */}
      <div className="grid-cols-2">
        {chapters.map((ch) => (
          <div key={ch.id} className="card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3>Kapitel {ch.id}</h3>
                <p className="text-sm text-muted">{ch.title}</p>
              </div>
              <div
                className={`badge ${ch.questionCount >= 50 ? 'badge-success' : ch.questionCount > 0 ? 'badge-warning' : 'badge-danger'}`}
              >
                {ch.questionCount} Fragen
              </div>
            </div>

            {/* Progress bar */}
            <div
              style={{
                background: 'var(--border-light)',
                borderRadius: 4,
                height: 6,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  background: ch.questionCount >= 50 ? 'var(--success)' : 'var(--warning)',
                  borderRadius: 4,
                  height: 6,
                  width: `${Math.min(100, (ch.questionCount / 50) * 100)}%`,
                  transition: 'width 0.3s ease',
                }}
              />
            </div>

            <div className="flex gap-4 text-xs text-muted">
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <HelpCircle size={12} /> {ch.questionCount} / 50
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <ImageIcon size={12} /> {ch.withImage} Bilder
              </span>
            </div>

            <div className="flex gap-2 mt-3">
              <span className="badge badge-success" style={{ fontSize: 11 }}>
                L: {ch.difficulties.easy}
              </span>
              <span className="badge badge-warning" style={{ fontSize: 11 }}>
                M: {ch.difficulties.medium}
              </span>
              <span className="badge badge-danger" style={{ fontSize: 11 }}>
                S: {ch.difficulties.hard}
              </span>
            </div>
          </div>
        ))}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
